import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Pencil, RefreshCw, Plus, Copy, Lock, LogOut } from "lucide-react";
import { api, ADMIN_TOKEN_KEY, CATEGORIES, DISCIPLINES, FOCUS } from "../lib/api";
import { Header } from "../components/Header";

const BLANK = {
  title: "", organization: "", category: "Grant", discipline: "Visual Arts", focus_tags: ["Women"],
  location: "", deadline: "", amount: "", description: "", url: "", status: "approved",
};

const STATUSES = ["pending", "approved", "rejected", "all"];

const SITE_FIELDS = [
  { key: "eyebrow", label: "Eyebrow (small line above headline)", type: "input" },
  { key: "headline", label: "Headline — black text (use Enter for line breaks)", type: "textarea" },
  { key: "headline_accent", label: "Headline — pink accent line", type: "input" },
  { key: "intro", label: "Intro paragraph", type: "textarea" },
  { key: "marquee", label: "Scrolling ribbon phrases (separate with  • )", type: "input" },
  { key: "footer_tagline", label: "Footer tagline", type: "input" },
  { key: "submit_heading", label: "Submit section — heading", type: "input" },
  { key: "submit_blurb", label: "Submit section — description", type: "textarea" },
  { key: "submit_button", label: "Submit section — button text", type: "input" },
];

function SiteTextPanel() {
  const [text, setText] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setText(data)); }, []);

  const set = (k, v) => setText((t) => ({ ...t, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", text);
      toast.success("Site text saved — refresh the directory to see it live");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const field = "w-full brutal-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink";
  if (!text) return null;

  return (
    <div className="brutal-border p-5 mb-8 bg-white" data-testid="site-text-panel">
      <button data-testid="site-text-toggle" onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center">
        <span className="label-caps">Site Text / Headline & Copy</span>
        <span className="label-caps text-pink">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="mt-5 space-y-4">
          {SITE_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="label-caps">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea data-testid={`site-${f.key}`} rows={f.key === "headline" ? 3 : 4} className={field}
                  value={text[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              ) : (
                <input data-testid={`site-${f.key}`} className={field}
                  value={text[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
            </div>
          ))}
          <button data-testid="site-save-btn" disabled={saving} onClick={save}
            className="label-caps bg-black text-white px-6 py-3 hover:bg-pink hover:text-black transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save Site Text"}
          </button>
        </div>
      )}
    </div>
  );
}

function OppForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    set("focus_tags", form.focus_tags.includes(t)
      ? form.focus_tags.filter((x) => x !== t)
      : [...form.focus_tags, t]);

  const save = async () => {
    if (!form.title || !form.organization) { toast.error("Title and organization are required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/opportunities/${form.id}`, form);
        toast.success("Opportunity updated");
      } else {
        await api.post("/admin/opportunities", form);
        toast.success("Opportunity created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const field = "w-full brutal-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink";

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center overflow-auto p-4" data-testid="opp-form-modal">
      <div className="bg-white brutal-border brutal-shadow w-full max-w-2xl my-8 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-display font-black text-2xl uppercase tracking-tight">{form.id ? "Edit" : "New"} Opportunity</h3>
          <button data-testid="form-close-btn" onClick={onClose}><X /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label-caps">Title *</label>
            <input data-testid="form-title" className={field} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Organization *</label>
            <input data-testid="form-org" className={field} value={form.organization} onChange={(e) => set("organization", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Category</label>
            <select data-testid="form-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Discipline</label>
            <select data-testid="form-discipline" className={field} value={form.discipline} onChange={(e) => set("discipline", e.target.value)}>
              {DISCIPLINES.filter((d) => d !== "All").map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Location</label>
            <input data-testid="form-location" className={field} value={form.location} onChange={(e) => set("location", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Deadline</label>
            <input data-testid="form-deadline" className={field} value={form.deadline} onChange={(e) => set("deadline", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Amount</label>
            <input data-testid="form-amount" className={field} value={form.amount} onChange={(e) => set("amount", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Apply URL</label>
            <input data-testid="form-url" className={field} value={form.url} onChange={(e) => set("url", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label-caps">Focus Tags</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {["Women", "LGBTQ+", "BIPOC"].map((t) => (
                <button key={t} type="button" data-testid={`form-tag-${t}`} onClick={() => toggleTag(t)}
                  className={`label-caps px-3 py-1.5 border-2 border-black ${form.focus_tags.includes(t) ? "bg-pink text-black" : "bg-white"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="label-caps">Description</label>
            <textarea data-testid="form-description" rows={4} className={field} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button data-testid="form-save-btn" disabled={saving} onClick={save}
            className="label-caps bg-black text-white px-6 py-3 hover:bg-pink hover:text-black transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="form-cancel-btn" onClick={onClose} className="label-caps border-2 border-black px-6 py-3 hover:bg-black hover:text-white transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      setError(err.response?.status === 401 ? "Incorrect password." : "Login failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <div className="App bg-white">
      <Header />
      <div className="min-h-[70vh] flex items-center justify-center p-4">
        <div data-testid="admin-login-card" className="bg-white brutal-border brutal-shadow max-w-md w-full p-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-pink border-2 border-black mb-5">
            <Lock size={26} />
          </div>
          <p className="label-caps text-pink mb-2">Curation Dashboard</p>
          <h2 className="font-display font-black uppercase tracking-tighter text-3xl mb-6">Admin access</h2>
          <form onSubmit={submit} className="space-y-3">
            <input
              data-testid="admin-password-input"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full brutal-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink"
            />
            {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading || !password}
              className="w-full label-caps bg-black text-white py-3 hover:bg-pink hover:text-black transition-colors disabled:opacity-50"
            >
              {loading ? "Unlocking…" : "Unlock Dashboard"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordPanel() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally { setSaving(false); }
  };

  const field = "w-full brutal-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink";
  return (
    <div className="brutal-border p-5 mb-8 bg-white" data-testid="password-panel">
      <button data-testid="password-toggle" onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center">
        <span className="label-caps">Account / Change Password</span>
        <span className="label-caps text-pink">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="mt-5 space-y-4 max-w-md">
          <div>
            <label className="label-caps">Current password</label>
            <input data-testid="pw-current" type="password" className={field} value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div>
            <label className="label-caps">New password</label>
            <input data-testid="pw-new" type="password" className={field} value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <button data-testid="pw-save-btn" disabled={saving || !current || !next} onClick={save}
            className="label-caps bg-black text-white px-6 py-3 hover:bg-pink hover:text-black transition-colors disabled:opacity-50">
            {saving ? "Updating…" : "Update Password"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(() => {
    try { return !!localStorage.getItem(ADMIN_TOKEN_KEY); } catch { return false; }
  });
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, grants: 0, jobs: 0 });
  const [signups, setSignups] = useState({ total: 0, existing: 0, new: 0 });
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [editing, setEditing] = useState(null);

  const logout = useCallback(() => {
    try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
    setAuthed(false);
  }, []);

  const embedUrl = `${window.location.origin}/embed`;
  const embedCode = `<div style="max-width:1200px;margin:0 auto;">
  <iframe id="rawfemme-opps" src="${embedUrl}" title="RawFemme Opportunities"
    style="width:100%;border:0;min-height:900px;" scrolling="no"></iframe>
</div>
<script>
  window.addEventListener("message", function (e) {
    if (e.data && e.data.type === "rawfemme-embed-height") {
      var f = document.getElementById("rawfemme-opps");
      if (f) f.style.height = e.data.height + "px";
    }
  });
</script>`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s, subs] = await Promise.all([
        api.get("/admin/opportunities", { params: { status: tab } }),
        api.get("/stats"),
        api.get("/admin/subscribers/count"),
      ]);
      setItems(list.data);
      setStats(s.data);
      setSignups(subs.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired — please log in again");
        logout();
        return;
      }
    }
    setLoading(false);
  }, [tab, logout]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  const setStatus = async (id, status) => {
    await api.patch(`/admin/opportunities/${id}/status`, { status });
    toast.success(`Marked ${status}`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/opportunities/${id}`);
    toast.success("Deleted");
    load();
  };
  const scrape = async () => {
    setScraping(true);
    try {
      const { data } = await api.post("/admin/scrape");
      toast.success(`Scanned ${data.scanned} · ${data.new_pending} new pending`);
      setTab("pending");
      load();
    } catch { toast.error("Scrape failed"); }
    finally { setScraping(false); }
  };

  const stat = (label, val, id) => (
    <div data-testid={id} className="brutal-border p-4 bg-white">
      <p className="font-display font-black text-3xl">{val}</p>
      <p className="label-caps text-black/60 mt-1">{label}</p>
    </div>
  );

  return (
    <div className="App bg-white">
      <Header />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
          <div>
            <p className="label-caps text-pink mb-2">Curation Dashboard</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl">Admin</h1>
          </div>
          <div className="flex gap-3">
            <button data-testid="scrape-btn" onClick={scrape} disabled={scraping}
              className="label-caps flex items-center gap-2 bg-pink text-black px-5 py-3 border-2 border-black brutal-shadow-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50">
              <RefreshCw size={16} className={scraping ? "animate-spin" : ""} /> {scraping ? "Scraping…" : "Run Scrape"}
            </button>
            <button data-testid="new-opp-btn" onClick={() => setEditing(BLANK)}
              className="label-caps flex items-center gap-2 bg-black text-white px-5 py-3 hover:bg-pink hover:text-black transition-colors">
              <Plus size={16} /> New
            </button>
            <button data-testid="admin-logout-btn" onClick={logout}
              className="label-caps flex items-center gap-2 bg-white text-black px-5 py-3 border-2 border-black hover:bg-black hover:text-white transition-colors">
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {stat("Live", stats.total, "stat-total")}
          {stat("Pending Review", stats.pending, "stat-pending")}
          {stat("Newsletter Signups", signups.total, "stat-signups")}
          {stat("Grants", stats.grants, "stat-grants")}
          {stat("Jobs", stats.jobs, "stat-jobs")}
        </div>

        {/* Site text + account */}
        <SiteTextPanel />
        <ChangePasswordPanel />

        <div className="brutal-border p-5 mb-8 bg-white">
          <div className="flex justify-between items-center mb-2">
            <p className="label-caps">WordPress Embed Code</p>
            <button data-testid="copy-embed-btn" onClick={async () => {
              try {
                if (navigator.clipboard && window.isSecureContext) {
                  await navigator.clipboard.writeText(embedCode);
                } else {
                  const ta = document.createElement("textarea");
                  ta.value = embedCode; ta.style.position = "fixed"; ta.style.opacity = "0";
                  document.body.appendChild(ta); ta.select();
                  document.execCommand("copy"); document.body.removeChild(ta);
                }
                toast.success("Embed code copied");
              } catch { toast.error("Copy failed – select and copy manually"); }
            }}
              className="label-caps flex items-center gap-1.5 hover:text-pink"><Copy size={14} /> Copy</button>
          </div>
          <code className="block text-xs bg-black text-white p-3 overflow-x-auto whitespace-pre">{embedCode}</code>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b-2 border-black pb-4">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`tab-${s}`} onClick={() => setTab(s)}
              className={`label-caps px-4 py-2 border-2 border-black ${tab === s ? "bg-black text-white" : "bg-white hover:bg-pink"}`}>
              {s}{s === "pending" && stats.pending ? ` (${stats.pending})` : ""}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="label-caps text-black/50 py-16 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p data-testid="admin-empty" className="label-caps text-black/50 py-16 text-center">Nothing here yet.</p>
        ) : (
          <div className="space-y-3">
            {items.map((o) => (
              <div key={o.id} data-testid={`admin-row-${o.id}`} className="brutal-border p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 mb-1">
                    <span className="label-caps px-2 py-0.5 bg-black text-white">{o.category}</span>
                    <span className={`label-caps px-2 py-0.5 ${o.status === "approved" ? "bg-pink text-black" : o.status === "pending" ? "bg-white border-2 border-black" : "bg-black/20"}`}>{o.status}</span>
                    <span className="label-caps text-black/50">{o.source}</span>
                  </div>
                  <p className="font-display font-bold text-lg truncate">{o.title}</p>
                  <p className="text-sm text-black/60">{o.organization} · {o.deadline}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {o.status !== "approved" && (
                    <button data-testid={`approve-${o.id}`} onClick={() => setStatus(o.id, "approved")} title="Approve"
                      className="p-2 border-2 border-black hover:bg-pink transition-colors"><Check size={16} /></button>
                  )}
                  {o.status !== "rejected" && (
                    <button data-testid={`reject-${o.id}`} onClick={() => setStatus(o.id, "rejected")} title="Reject"
                      className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"><X size={16} /></button>
                  )}
                  <button data-testid={`edit-${o.id}`} onClick={() => setEditing(o)} title="Edit"
                    className="p-2 border-2 border-black hover:bg-pink transition-colors"><Pencil size={16} /></button>
                  <button data-testid={`delete-${o.id}`} onClick={() => remove(o.id)} title="Delete"
                    className="p-2 border-2 border-black hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <OppForm initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
